import type { CSSProperties } from 'react';

export type SectionPaddingValues = {
  paddingTop: number;
  paddingRight: number;
  paddingBottom: number;
  paddingLeft: number;
};

const DEFAULT_SECTION_PADDING: SectionPaddingValues = {
  paddingTop: 16,
  paddingRight: 20,
  paddingBottom: 16,
  paddingLeft: 20,
};

/** Fallback paddings (px) matching current storefront Tailwind defaults per section type. */
export const SECTION_PADDING_DEFAULTS: Record<string, SectionPaddingValues> = {
  DEFAULT: DEFAULT_SECTION_PADDING,
  CONTACT: { paddingTop: 64, paddingRight: 20, paddingBottom: 64, paddingLeft: 20 },
  RICH_TEXT: DEFAULT_SECTION_PADDING,
  CUSTOM_HTML: DEFAULT_SECTION_PADDING,
  IMAGE_TEXT_CONTAINER: { paddingTop: 0, paddingRight: 20, paddingBottom: 0, paddingLeft: 20 },
  ICONS_WITH_DESCRIPTION: { paddingTop: 24, paddingRight: 0, paddingBottom: 24, paddingLeft: 0 },
  FEATURE_ICON_GRID: { paddingTop: 24, paddingRight: 0, paddingBottom: 24, paddingLeft: 0 },
  IMAGE_CARDS_WITH_TEXT: { paddingTop: 24, paddingRight: 16, paddingBottom: 24, paddingLeft: 16 },
  RECIPE_PREPARATION_STEPS: DEFAULT_SECTION_PADDING,
  RECIPE_INGREDIENTS: DEFAULT_SECTION_PADDING,
  RECIPE_HERO_BANNER: DEFAULT_SECTION_PADDING,
  CONTENT_CARD_GRID: { paddingTop: 32, paddingRight: 12, paddingBottom: 32, paddingLeft: 12 },
  RECIPE_CARD_GRID: { paddingTop: 48, paddingRight: 20, paddingBottom: 48, paddingLeft: 20 },
  SIMILAR_RECIPES: { paddingTop: 24, paddingRight: 20, paddingBottom: 24, paddingLeft: 20 },
  STORE_LOCATIONS: { paddingTop: 40, paddingRight: 20, paddingBottom: 40, paddingLeft: 20 },
  HERO_BANNER_WITH_CAROUSEL: {
    paddingTop: 16,
    paddingRight: 16,
    paddingBottom: 16,
    paddingLeft: 16,
  },
  FULL_BLEED_HERO: { paddingTop: 48, paddingRight: 0, paddingBottom: 64, paddingLeft: 0 },
  VIDEO_HERO: { paddingTop: 64, paddingRight: 0, paddingBottom: 64, paddingLeft: 0 },
  PRODUCT_SHOWCASE: { paddingTop: 32, paddingRight: 16, paddingBottom: 32, paddingLeft: 16 },
  IMAGE_GRID: { paddingTop: 40, paddingRight: 16, paddingBottom: 40, paddingLeft: 16 },
  CATEGORY_GRID: { paddingTop: 40, paddingRight: 16, paddingBottom: 40, paddingLeft: 16 },
  CATEGORY_CAROUSEL: { paddingTop: 40, paddingRight: 16, paddingBottom: 40, paddingLeft: 16 },
  IMAGE_BANNER_WITH_TEXT: { paddingTop: 96, paddingRight: 16, paddingBottom: 96, paddingLeft: 16 },
  BLOG_HERO: { paddingTop: 96, paddingRight: 16, paddingBottom: 96, paddingLeft: 16 },
  FULL_WIDTH_BANNER_IMAGE: { paddingTop: 0, paddingRight: 20, paddingBottom: 0, paddingLeft: 20 },
};

function parsePaddingValue(value: unknown, fallback: number): number {
  if (value === '' || value === null || value === undefined) {
    return fallback;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback;
}

export function getDefaultSectionPadding(sectionType: string): SectionPaddingValues {
  return SECTION_PADDING_DEFAULTS[sectionType] ?? DEFAULT_SECTION_PADDING;
}

export function getDefaultSectionPaddingFields(
  sectionType: string,
): Record<keyof SectionPaddingValues, number> {
  return { ...getDefaultSectionPadding(sectionType) };
}

export function resolveSectionPadding(
  sectionType: string,
  sectionStyle: Record<string, unknown> | undefined,
): SectionPaddingValues {
  const defaults = getDefaultSectionPadding(sectionType);

  return {
    paddingTop: parsePaddingValue(sectionStyle?.paddingTop, defaults.paddingTop),
    paddingRight: parsePaddingValue(sectionStyle?.paddingRight, defaults.paddingRight),
    paddingBottom: parsePaddingValue(sectionStyle?.paddingBottom, defaults.paddingBottom),
    paddingLeft: parsePaddingValue(sectionStyle?.paddingLeft, defaults.paddingLeft),
  };
}

export function sectionPaddingToStyle(padding: SectionPaddingValues): CSSProperties {
  return {
    paddingTop: `${padding.paddingTop}px`,
    paddingRight: `${padding.paddingRight}px`,
    paddingBottom: `${padding.paddingBottom}px`,
    paddingLeft: `${padding.paddingLeft}px`,
  };
}

export function resolveSectionPaddingStyle(
  sectionType: string,
  sectionStyle: Record<string, unknown> | undefined,
): CSSProperties {
  return sectionPaddingToStyle(resolveSectionPadding(sectionType, sectionStyle));
}
